import { Action } from 'redux';
import * as shallowEqual from 'shallowequal';

import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import {
  isClearSpaceContentCountsAction, isContentCountsRequestSucceededAction,
  isContentCountsUpToDateAction, isDecrementContentCountAction,
  isIncrementContentCountAction,
  isUpdateSpaceInfoCountAction
} from '../../actions/spaces/ContentCountsActions';
import { isCreateNewProjectAction } from '../../actions/spaces/ContentExplorerCreateActions';
import { isFilterContentTypeAction } from '../../actions/spaces/ContentExplorerFilterActions';
import { isDeleteContentSucceeded } from '../../actions/spaces/DeleteContentAction';
import { isEnterSpaceAction } from '../../actions/spaces/EnterSpaceAction';
import { isFilterContentAction } from '../../actions/spaces/filters/FilterActions';
import { isMoveContentSuceededAction } from '../../actions/spaces/MoveContentsAction';
import { ContentCountsState, SpaceInfoTypeContentCounts } from '../../state/ContentCountsState';
import { SubspaceName } from '../../state/SpaceInfo';

export const initialContentCountsState: ContentCountsState = {
  counts: {
    site: null,
    project: null,
    workbook: null,
    datasource: null,
    view: null,
    flow: null,
    dataRole: null,
    lens: null,
    favorites: null,
    recents: null,
    recommendations: null,
    home: null,
    search: null,
    unifiedSearch: null,
    backgroundJobs: null,
    metric: null,
    group: null,
    siteuser: null,
    siteusers: null,
    serveruser: null,
    serverusers: null,
    sites: null,
    sitestatus: null,
    sitetasks: null,
    schedules: null,
    schedule: null,
    siteanalysis: null,
    serveranalysis: null,
    sitesettings: null,
    serversettings: null,
    serverstatus: null,
    servertasks: null,
    catalog: null,
    revisionhistory: null,
    groups: null,
    collections: null,
    collection: null,
    publishedConnection: null,
    publishedConnectionTable: null,
    starters: null,
    sharedWithMe: null,
    personal: null,
    resourceGovernanceSetting: null,
    externalAssets: null,
    connectedApplication: null,
  },
  isUpToDate: false
};

export function contentCountsReducer(
  previousState: ContentCountsState = initialContentCountsState,
  action: Action = null
): ContentCountsState {

  if (isContentCountsRequestSucceededAction(action)) {

    const counts = previousState.counts[action.spaceType] as SpaceInfoTypeContentCounts;
    if (counts && shallowEqual(counts, action.counts)) {
      return {
        ...previousState,
        isUpToDate: true
      };
    }

    return {
      ...previousState,
      isUpToDate: true,
      counts: {
        ...previousState.counts,
        [action.spaceType]: action.counts,
      }
    };
  }

  // The following are all workflows that will change the content counts
  if (isActionThatChangesContentCounts(action) && previousState.isUpToDate) {
    return {...previousState, isUpToDate: false};
  }

  // To avoid having to create EnterSubPlaceActions, allow changing of sub spaces to
  // explicitly say the counts are up to date, exclude the initial case
  // However this is an imperfect solution as enterSpace will trigger a render
  // and a fetch content counts call before dispatching this action.
  if (isContentCountsUpToDateAction(action) && previousState !== initialContentCountsState) {
    return {...previousState, isUpToDate: true};
  }

  if (isUpdateSpaceInfoCountAction(action)) {
    const counts = previousState.counts[action.spaceInfo.spaceType] as SpaceInfoTypeContentCounts;
    if (counts && counts[action.spaceInfo.subspaceName as SubspaceName | ExploreContentApi_ContentType] !== action.count) {
      return {
        ...previousState,
        counts: {
          ...previousState.counts,
          [action.spaceInfo.spaceType]: {
            ...previousState.counts[action.spaceInfo.spaceType],
            [action.spaceInfo.subspaceName]: action.count
          }
        }
      };
    }
  }

  if (isDecrementContentCountAction(action)) {
    // grab count for action.spaceType action.contentType and reduce by 1
    const counts = previousState.counts[action.spaceType] as SpaceInfoTypeContentCounts;
    if (counts && counts[action.subspaceName] > 0) {
      const updatedCount = counts[action.subspaceName] - 1;
      return {
        ...previousState,
        counts: {
          ...previousState.counts,
          [action.spaceType]: {
            ...previousState.counts[action.spaceType],
            [action.subspaceName]: updatedCount
          }
        }
      };
    }
  }

  if (isIncrementContentCountAction(action)) {
    // grab count for action.spaceType action.contentType and increase by 1
    const counts = previousState.counts[action.spaceType] as SpaceInfoTypeContentCounts;
    if (counts) {
      const updatedCount = counts[action.subspaceName] + 1;
      return {
        ...previousState,
        counts: {
          ...previousState.counts,
          [action.spaceType]: {
            ...previousState.counts[action.spaceType],
            [action.subspaceName]: updatedCount
          }
        }
      };
    }
  }

  if (isClearSpaceContentCountsAction(action)) {
    return {
      ...previousState,
      isUpToDate: false,
      counts: {
        ...previousState.counts,
        [action.spaceType]: null,
      }
    };
  }

  return previousState;
}

const isActionThatChangesContentCounts = (action: Action): boolean => {
  return isFilterContentAction(action)
      || isFilterContentTypeAction(action)
      || isMoveContentSuceededAction(action)
      || isDeleteContentSucceeded(action)
      || isEnterSpaceAction(action)
      || isCreateNewProjectAction(action)
      || isClearSpaceContentCountsAction(action);
};
